import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralApi } from '../api/referral';

const usd = (n?: number) => '$' + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const initials = (email?: string) => ((email || '?').split('@')[0].slice(0, 2) || '?').toUpperCase();

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

export default function Referrals() {
  const summary = useQuery({ queryKey: ['referral-me'], queryFn: referralApi.me });
  const referees = useQuery({ queryKey: ['referral-referees'], queryFn: referralApi.referees });
  const [level, setLevel] = useState<'L1' | 'L2'>('L1');

  const d = summary.data;
  const list = referees.data?.referees ?? [];

  async function share() {
    if (!d?.referralUrl) return;
    try {
      if (navigator.share) await navigator.share({ title: 'AltCoinsMoney', url: d.referralUrl });
      else await navigator.clipboard.writeText(d.referralUrl);
    } catch {}
  }

  return (
    <div className="space-y-5 pb-2">
      <div>
        <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>YOUR NETWORK</div>
        <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Referrals</h1>
      </div>

      {/* Share */}
      <Card className="p-5">
        {d?.referralUrl ? (
          <>
            <div className="text-sm mb-3 truncate" style={{ fontFamily: '"JetBrains Mono", monospace', color: '#c4b5fd' }}>{d.referralUrl}</div>
            <button onClick={share} className="w-full rounded-2xl py-3 text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>Share your link</button>
          </>
        ) : (
          <div className="text-sm" style={{ color: '#8B82A8' }}>Reach Bronze (spend $100) to unlock your referral link.</div>
        )}
      </Card>

      {/* Mini stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Clicks', value: d?.stats?.totalClicks ?? 0, color: '#22D3EE' },
          { label: 'Signups', value: d?.stats?.totalSignups ?? 0, color: '#FBBF24' },
          { label: 'Qualified', value: d?.stats?.qualifyingReferrals ?? 0, color: '#34D399' },
        ].map((s) => (
          <Card key={s.label} className="p-3 text-center">
            <div className="text-2xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: s.color }}>{s.value}</div>
            <div className="text-[11px] mt-1" style={{ color: '#8B82A8' }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* L1 / L2 toggle */}
      <div className="flex p-1 rounded-2xl" style={{ backgroundColor: '#1d1640' }}>
        {(['L1', 'L2'] as const).map((lv) => (
          <button key={lv} onClick={() => setLevel(lv)} className="flex-1 py-2 rounded-xl text-sm font-semibold"
            style={level === lv ? { background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', color: 'white' } : { color: '#8B82A8' }}>
            {lv === 'L1' ? 'Direct (L1)' : 'Indirect (L2)'}
          </button>
        ))}
      </div>

      {level === 'L1' ? (
        referees.isLoading ? (
          <p className="text-sm" style={{ color: '#8B82A8' }}>Loading…</p>
        ) : list.length === 0 ? (
          <Card className="p-5"><div className="text-sm" style={{ color: '#8B82A8' }}>No direct referrals yet.</div></Card>
        ) : (
          <div className="space-y-3">
            {list.map((ref) => (
              <Card key={ref.id} className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#1d1640' }}>{initials(ref.email)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{ref.email}</div>
                    <div className="text-[11px]" style={{ color: '#8B82A8' }}>{fmtDate(ref.joinedAt)} · {ref.qualifyingPurchases} buy{ref.qualifyingPurchases === 1 ? '' : 's'}</div>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full" style={{ backgroundColor: ref.kycStatus === 'APPROVED' ? 'rgba(52,211,153,0.15)' : 'rgba(139,130,168,0.15)', color: ref.kycStatus === 'APPROVED' ? '#34D399' : '#8B82A8' }}>
                    {ref.kycStatus === 'APPROVED' ? 'KYC ✓' : ref.kycStatus}
                  </span>
                </div>
                <div className="flex justify-between mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div><div className="text-[10px]" style={{ color: '#8B82A8' }}>Spent</div><div className="text-sm font-semibold">{usd(ref.totalPurchasedUsdt)}</div></div>
                  <div className="text-right"><div className="text-[10px]" style={{ color: '#8B82A8' }}>You earned</div><div className="text-sm font-semibold" style={{ color: '#34D399' }}>{usd(ref.youEarnedUsdt)}</div></div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : (
        <Card className="p-5">
          <div className="text-sm mb-2" style={{ color: '#c4b5fd' }}>Indirect (L2) earnings</div>
          <div className="text-3xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: '#a78bfa' }}>{usd(d?.earnings?.totalIndirectUsdt)}</div>
          <div className="text-xs mt-2" style={{ color: '#8B82A8' }}>Total earned from your referrals’ referrals. A person-by-person L2 list isn’t available from the API yet — we can add that endpoint later if you want it.</div>
        </Card>
      )}
    </div>
  );
}